/**
 * 🟦 V9.3 — PWA Update Banner.
 *
 * Detecta cuando hay un Service Worker en estado `waiting` (versión nueva
 * lista para activarse) y muestra un banner discreto que permite al usuario
 * activar la actualización con un click. Sin click → el banner queda visible
 * pero no fuerza la recarga.
 *
 * Flujo:
 * 1. Al montar, hace `navigator.serviceWorker.getRegistration()`.
 * 2. Suscribe a:
 *    - `registration.waiting` ya presente (SW listo cuando se cargó la app).
 *    - Evento `updatefound` → nuevo SW entra en `installing` → cuando pasa
 *      a `installed` con `navigator.serviceWorker.controller` existente
 *      (no es la primera instalación), aparece el banner.
 * 3. Click "Actualizar": postMessage `{type:'SKIP_WAITING'}` al waiting SW.
 *    - El SW responde con skipWaiting() + clients.claim().
 *    - El navegador dispara `controllerchange` → `window.location.reload()`.
 * 4. Solo se muestra UNA vez por SW version (si el user cierra el banner
 *    con la X, queda dismissed hasta que aparezca otra version).
 *
 * El componente es i18n-aware (ES/EN) y no rompe nada cuando navigator.serviceWorker
 * no existe (servers HTTP sin HTTPS / browsers viejos).
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const PwaUpdateBanner = () => {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const t = isEs ? {
    title: 'Nueva versión disponible',
    body: 'Toca para actualizar con las últimas mejoras.',
    update: 'Actualizar',
    dismiss: 'Cerrar',
    updating: 'Actualizando…',
    changelogTitle: 'Qué cambió',
    moreChanges: 'Ver más cambios',
    modalTitle: (v) => `Cambios en la versión ${v}`,
    close: 'Cerrar',
  } : {
    title: 'New version available',
    body: 'Tap to update with the latest improvements.',
    update: 'Update',
    dismiss: 'Dismiss',
    updating: 'Updating…',
    changelogTitle: 'What changed',
    moreChanges: 'View more changes',
    modalTitle: (v) => `Changes in version ${v}`,
    close: 'Close',
  };

  const [waitingWorker, setWaitingWorker] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [changelog, setChangelog] = useState(null);  // 🟦 V9.3.1 — { version, changes }
  const [showFullModal, setShowFullModal] = useState(false);
  const dismissedVersionRef = useRef(null);

  // Set waitingWorker SOLO si todavía no fue dismissed para esta versión.
  const offerWaiting = useCallback((sw) => {
    if (!sw) return;
    const scriptURL = sw.scriptURL || '';
    if (dismissedVersionRef.current === scriptURL) return;
    setWaitingWorker(sw);
    setDismissed(false);
  }, []);

  // 🟦 V9.3 — Flag de testing para QA visual: solo activable explícitamente con
  // `?pwa-update-test=1`. NUNCA se dispara en producción salvo que el QA lo invoque.
  // Crea un waiting worker simulado para validar la UI del banner sin requerir
  // un deploy real del Service Worker.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('pwa-update-test') === '1') {
        const mockedMessages = [];
        const fake = {
          scriptURL: '/sw.js?v=qa-test',
          state: 'installed',
          postMessage: (msg) => {
            mockedMessages.push(msg);
            window.__pwaSwMessages = mockedMessages;
          },
          addEventListener: () => {},
        };
        offerWaiting(fake);
      }
    } catch (e) { /* noop */ }
  }, [offerWaiting]);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return undefined;
    }

    let cancelled = false;
    let registrationRef = null;

    const trackInstalling = (sw) => {
      if (!sw) return;
      sw.addEventListener('statechange', () => {
        // Cuando un nuevo SW se instala AND ya hay un controller, hay nueva versión.
        if (sw.state === 'installed' && navigator.serviceWorker.controller) {
          offerWaiting(sw);
        }
      });
    };

    const attach = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg || cancelled) return;
        registrationRef = reg;

        // Si ya hay un waiting al cargar, mostrar de una.
        if (reg.waiting && navigator.serviceWorker.controller) {
          offerWaiting(reg.waiting);
        }

        // Si hay un installing en progreso, trackearlo.
        if (reg.installing) {
          trackInstalling(reg.installing);
        }

        // Cualquier update futuro.
        reg.addEventListener('updatefound', () => {
          trackInstalling(reg.installing);
        });

        // Cuando el controller cambia (skipWaiting consumado), recargar.
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (cancelled) return;
          // Pequeño delay para que el SW reclame antes del reload.
          setTimeout(() => window.location.reload(), 50);
        });
      } catch (e) {
        // best-effort, no romper la app si falla.
      }
    };

    attach();

    return () => {
      cancelled = true;
    };
  }, [offerWaiting]);

  const handleUpdate = () => {
    if (!waitingWorker) return;
    setUpdating(true);
    try {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
      // Fallback por si controllerchange no dispara en algunos navegadores.
      setTimeout(() => {
        if (typeof window !== 'undefined') window.location.reload();
      }, 3000);
    } catch (e) {
      setUpdating(false);
    }
  };

  const handleDismiss = () => {
    if (waitingWorker && waitingWorker.scriptURL) {
      dismissedVersionRef.current = waitingWorker.scriptURL;
    }
    setDismissed(true);
    setShowFullModal(false);
  };

  // 🟦 V9.3.1 — Fetch changelog.json cuando hay nueva versión disponible.
  useEffect(() => {
    if (!waitingWorker || dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/changelog.json', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data && Array.isArray(data.changes) && data.changes.length > 0) {
          setChangelog({ version: data.version || '', changes: data.changes });
        }
      } catch (_) { /* falla silenciosa: el banner sigue visible sin lista */ }
    })();
    return () => { cancelled = true; };
  }, [waitingWorker, dismissed]);

  if (!waitingWorker || dismissed) return null;

  const previewChanges = changelog ? changelog.changes.slice(0, 5) : [];
  const hasMore = changelog && changelog.changes.length > 5;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] w-[92vw] max-w-md px-4 py-3 rounded-xl bg-slate-900/95 border border-indigo-500/40 shadow-2xl backdrop-blur-md text-white animate-[fadeInUp_240ms_ease-out]"
        data-testid="pwa-update-banner"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <div className="shrink-0 p-2 rounded-lg bg-indigo-500/20 border border-indigo-500/30">
            <Sparkles className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">{t.title}</p>
            <p className="text-[11px] text-slate-300 leading-snug">{t.body}</p>
          </div>
          <button
            onClick={handleUpdate}
            disabled={updating}
            className="shrink-0 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-semibold flex items-center gap-1.5"
            data-testid="pwa-update-banner-action"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${updating ? 'animate-spin' : ''}`} />
            {updating ? t.updating : t.update}
          </button>
          <button
            onClick={handleDismiss}
            className="shrink-0 p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="pwa-update-banner-dismiss"
            aria-label={t.dismiss}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {/* 🟦 V9.3.1 — Mini changelog (3–5 items) */}
        {previewChanges.length > 0 && (
          <div className="mt-3 pt-3 border-t border-slate-700/50" data-testid="pwa-update-changelog">
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">
              {t.changelogTitle}{changelog.version ? ` · v${changelog.version}` : ''}
            </p>
            <ul className="space-y-1 text-[11px] text-slate-200 leading-snug">
              {previewChanges.map((c, i) => (
                <li key={i} className="flex gap-2" data-testid={`pwa-update-changelog-item-${i}`}>
                  <span className="text-indigo-400 shrink-0">•</span>
                  <span className="min-w-0">{c}</span>
                </li>
              ))}
            </ul>
            {hasMore && (
              <button
                onClick={() => setShowFullModal(true)}
                className="mt-2 text-[10px] text-indigo-400 hover:text-indigo-300 underline"
                data-testid="pwa-update-changelog-more"
              >
                {t.moreChanges} ({changelog.changes.length - 5}+)
              </button>
            )}
          </div>
        )}
      </div>

      {/* 🟦 V9.3.1 — Modal con changelog completo */}
      {showFullModal && changelog && (
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowFullModal(false)}
          data-testid="pwa-update-changelog-modal"
        >
          <div
            className="bg-slate-900 border border-indigo-500/40 rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={t.modalTitle(changelog.version)}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/40">
              <p className="text-sm font-semibold text-white">{t.modalTitle(changelog.version)}</p>
              <button
                onClick={() => setShowFullModal(false)}
                className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-800"
                data-testid="pwa-update-changelog-modal-close"
                aria-label={t.close}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="flex-1 overflow-y-auto px-5 py-4 space-y-2 text-xs text-slate-200">
              {changelog.changes.map((c, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-indigo-400 shrink-0 mt-0.5">•</span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaUpdateBanner;
